import { useDispatch, useSelector } from 'react-redux';
import { Avatar, Button, Grid, Header, Modal, Popup, Row, styles, Surface, Tab, Table, Text, useTheme } from '../../tmwx-design-web/src';
import { usePermissions } from 'tmwx-design-common';
import { useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import HandleUser from '../../components/portal/HandleUser';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../gui-common/redux/store';
import { User } from '../../gui-common/redux/data/user';
import { setSnackbarData } from '../../gui-common/redux/action/snackbarAction';
import { Group } from '../../gui-common/redux/data/group';
import { Role } from '../../gui-common/redux/data/role';
import { changeUserActive, clearUserResponse, getUserList } from '../../gui-common/redux/action/portal/userAction';
import { getRoleList } from '../../gui-common/redux/action/portal/roleAction';
import { ResponseBase } from 'gui-common/redux/data';

const Users = () => {
    const { t } = useTranslation()
    const dispatch: any = useDispatch();
    const { hasRights } = usePermissions()
    const { roundness, padding } = useTheme()
    const userList = useSelector<RootState, User[]>(state => state.portalUser.userList);
    const userLoading = useSelector<RootState, boolean>(state => state.portalUser.isLoading);
    const response = useSelector<RootState, ResponseBase | null>(state => state.portalUser.response);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false)
    const [showGroups, setShowGroups] = useState<boolean>(false)
    const [showRoles, setShowRoles] = useState<boolean>(false)
    const [isCopy, setIsCopy] = useState<boolean>(false)

    const closeDialog = () => {
        setSelectedUser(null)
        setIsCopy(false)
        setDialogOpen(false)
        setShowGroups(false)
        setShowRoles(false)
    }

    const handleShow = (user: any, isRole: boolean) => {
        setSelectedUser(user)

        if (isRole) {
            setShowRoles(true)
        } else {
            setShowGroups(true)
        }
    }

    const handleEdit = (user: any) => {
        setSelectedUser(user)
        setIsCopy(false)
        setDialogOpen(true)
    }

    const handleAdd = () => {
        setSelectedUser(null)
        setIsCopy(false)
        setDialogOpen(true)
    }

    /* HANDLE DEACTIVATE RESPONSE */
    useEffect(() => {
        if (response) {
            if (response.responseCode === 'SUCCESS') {
                dispatch(getUserList())
                dispatch(clearUserResponse())
            }
        }
    }, [response])

    const columns = useMemo<ColumnDef<User>[]>(
        () => [
            {
                accessorKey: 'subsystemList',
                meta: t('subsystem'),
                header: () => <Text bold>{t('subsystem')}</Text>,
                cell: ({ row }) => (
                    <div style={styles.row}>
                        {row.original.subsystemList && row.original.subsystemList.map((item: any, index: number) => (
                            <Text key={index}>{`${item.id}${index + 1 !== row.original.subsystemList.length ? ',' : ''}`}</Text>
                        ))}
                    </div>
                ),
                size: 100
            },
            {
                accessorKey: 'username',
                meta: t('username'),
                header: () => <Text bold>{t('username')}</Text>
            },
            {
                accessorKey: 'name',
                meta: t('name'),
                header: () => <Text bold>{t('name')}</Text>
            },
            {
                accessorKey: 'email',
                meta: t('email'),
                header: () => <Text bold>{t('email')}</Text>,
                size: 250,
                cell: (cell: any) => (
                    <Button
                        status='neutral'
                        uppercase={false}
                        size='small'
                        onPress={() => {
                            navigator.clipboard.writeText(cell.getValue())
                            dispatch(setSnackbarData({
                                status: 'success',
                                text: t('copyToClipboard')
                            }))
                        }}>{cell.getValue()}</Button>
                )
            },
            {
                accessorKey: 'authType',
                meta: t('auth'),
                header: () => <Text bold>{t('auth')}</Text>,
                size: 120
            },
            {
                accessorKey: 'inactive',
                meta: t('status'),
                header: () => <Text bold>{t('status')}</Text>,
                cell: (cell: any) => (
                    <div>
                        {!cell.getValue() ? <Button status='success' size='small' uppercase={false} onPress={() => { }}>{t('active')}</Button> : <Button status='warning' size='small' uppercase={false} onPress={() => { }}>{t('inactive')}</Button>}
                    </div>
                ),
                size: 100
            },
            {
                accessorKey: 'roleList',
                meta: t('portal.rolesText'),
                header: () => <Text bold>{t('portal.rolesText')}</Text>,
                cell: ({ row }) => (
                    <Button
                        variant='contained'
                        status='neutral'
                        size='small'
                        uppercase={false}
                        onPress={() => handleShow(row.original, true)}
                    >
                        {t('showMore')}
                    </Button>
                )
            },
            /* {
                accessorKey: 'groupList',
                meta: t('portal.groupsText'),
                header: () => <Text bold centered>{t('portal.groupsText')}</Text>,
                cell: ({ row }) => (
                    <Button
                        variant='contained'
                        status='neutral'
                        size='small'
                        uppercase={false}
                        onPress={() => handleShow(row.original, false)}
                    >
                        {t('showMore')}
                    </Button>
                )
            }, */
            {
                header: () => <Text bold>{t('actions')}</Text>,
                id: 'actions',
                meta: t('actions'),
                type: 'actions',
                size: 80,
                cell: ({ row }) => (
                    <div style={{ ...styles.row }}>
                        {(hasRights(['portal_modul_users_edit']) && row.original.authType === 'MANUAL') && (
                            <Popup
                                style={styles.marginRight4}
                                triggerComponent={
                                    <Button
                                        iconLeft={'edit_square_outlined'}
                                        status='primary'
                                        size='small'
                                        onPress={() => handleEdit(row.original)}
                                    />
                                }
                                type='hover'
                            >
                                <Surface style={{ padding: padding }} elevation={1}>
                                    <Text>{t('portal.users.modifyUser')}</Text>
                                </Surface>
                            </Popup>
                        )}

                        {hasRights(['portal_modul_users_delete']) && (
                            <Popup
                                triggerComponent={
                                    <Button
                                        iconLeft={row.original.inactive ? 'play_arrow' : 'stop'}
                                        status={row.original.inactive ? 'success' : 'warning'}
                                        size='small'
                                        onPress={() => dispatch(changeUserActive(row.original.id))}
                                    />
                                }
                                type='hover'
                            >
                                <Surface style={{ padding: padding }} elevation={1}>
                                    <Text>{row.original.inactive ? t('activate') : t('deactivate')}</Text>
                                </Surface>
                            </Popup>
                        )}
                    </div>
                ),
                enableColumnOrdering: false,
                enableColumnActions: false,
                enableSorting: false,
                enableResizing: false,
                enableHiding: false,
            },
        ],
        [dispatch, hasRights, padding, t]
    );

    useEffect(() => {
        if (hasRights(["portal_modul_users_read"])) {
            dispatch(getUserList())
            dispatch(getRoleList())
        }
    }, [dispatch, hasRights])

    return (
        <Surface style={{ height: '100vh', paddingTop: 8, paddingBottom: 8 }}>
            <Grid.Container style={{ borderRadius: roundness }} maxWidth='xl' elevation={0} centered>
                {/* <Grid.Col m={6} lg={6} xl={6}>
                <PortalStatisticsCard
                    iconName='person_outlined'
                    title={t('portal.usersText')}
                    description={t('portal.numOfUsers')}
                    value={userList.length}
                />
            </Grid.Col>
            <Grid.Col m={6} lg={6} xl={6}>
                <PortalStatisticsCard
                    iconName='check'
                    title={t('portal.users.activeUsers')}
                    description={t('portal.users.activeNumOfUsers')}
                    value={getFilteredArrayLength(userList, 'inactive', false)}
                />
            </Grid.Col> */}
                <Grid.Col>
                    <Table
                        id='USERS_TABLE'
                        columns={columns}
                        data={userList}
                        loading={userLoading}
                        /* maxData={100}
                        fetchData={getData} */
                        dense
                        useGlobalFilter
                        useColumnVisibility
                        add={hasRights(['portal_modul_users_create']) ? () => handleAdd() : undefined}
                        refreshData={() => dispatch(getUserList())}
                        labels={{
                            add: t('add'),
                            deletePreferences: t('deletePreferences'),
                            jumpHere: t('jumpHere'),
                            page: t('page'),
                            refresh: t('refresh'),
                            row: t('row'),
                            rowsSelected: t('rowsSelected'),
                            savePreferencies: t('savePreferences'),
                            searchData: t('searchData'),
                            selectCurrentPageData: t('selectCurrentPageData'),
                            showAll: t('showAll'),
                            sizing: t('sizing')
                        }}
                    />
                </Grid.Col>
            </Grid.Container>

            <Modal
                visible={showGroups || showRoles}
                type='dialog'
                dialogMaxWidth='xs'
                contentElevation={0}
                closeModal={closeDialog}
                header={(
                    <Header
                        subtitle={selectedUser?.name}
                        title={showGroups ? t('portal.groupsText') : t('portal.rolesText')}
                        actionRight={<Button iconLeft={'clear'} status='neutral' variant='ghost' onPress={closeDialog} />}
                    />
                )}
            >
                {showGroups && (
                    <Surface>
                        {selectedUser?.groupList.map((item: Group, index: number) => (
                            <Row
                                key={index}
                                avatar={<Avatar iconName={'groups_2_outlined'} />}
                                title={item.code}
                                subtitle={`${item.description}`}
                            />
                        ))}
                    </Surface>
                )}
                {showRoles && (
                    <Surface>
                        {selectedUser?.roleList.map((item: Role, index: number) => (
                            <Row
                                key={index}
                                avatar={<Avatar iconName={'badge_outlined'} />}
                                title={item.name}
                                subtitle={`${item.description} - ${item.inactive ? t('inactive') : t('active')}`}
                            />
                        ))}
                    </Surface>
                )}
            </Modal>

            <Modal
                visible={dialogOpen}
                type='dialog'
                dialogMaxWidth='s'
                contentElevation={0}
                closeModal={closeDialog}
                header={(
                    <Header
                        title={selectedUser && !isCopy ? selectedUser.name : t('portal.users.addUser')}
                        actionRight={<Button iconLeft={'clear'} status='neutral' variant='ghost' onPress={closeDialog} />}
                    />
                )}
            >
                <HandleUser actionType={!selectedUser ? 'add' : (!isCopy ? 'edit' : 'add')} item={selectedUser} onClose={closeDialog} />
            </Modal>
        </Surface>
    );
};

export default Users;